type UrlBuilderOptions = {
  /**
   * Protocolo a ser usado (padrão: 'auto' que detecta automaticamente)
   */
  protocol?: 'http' | 'https' | 'auto';

  /**
   * Forçar barra no final (padrão: true para URLs canônicas)
   */
  trailingSlash?: boolean;

  /**
   * Parâmetros de query string (opcional)
   */
  query?: Record<string, string | number | boolean>;

  /**
   * Subdomínio (opcional)
   */
  subdomain?: string;

  /**
   * Usar www ou não (padrão: false)
   */
  useWww?: boolean;
};

/**
 * Constrói URLs canônicas consistentes para todos os projetos
 * @example
 * buildCanonicalUrl('blog/sobre', { trailingSlash: true })
 * // → 'https://webproject.com.br/blog/sobre/'
 */
export const buildCanonicalUrl = (
  path: string,
  options: UrlBuilderOptions = {}
): string => {
  const isProd = process.env.NODE_ENV === 'production';
  const domain = process.env.NEXT_PUBLIC_SITE_DOMAIN || 'webproject.com.br';

  const protocol = options.protocol === 'auto'
    ? (isProd ? 'https://' : 'http://')
    : `${options.protocol || 'https'}://`;

  const host = [
    options.subdomain ? `${options.subdomain}.` : '',
    options.useWww ? 'www.' : '',
    domain
  ].join('');

  let normalizedPath = path
    .replace(/^\/+/, '')
    .replace(/\/+/g, '/')
    .replace(/\/$/, '');

  if (options.trailingSlash !== false && !normalizedPath.match(/\.[a-z]{2,}$/i)) {
    normalizedPath += '/';
  }

  const queryString = options.query
    ? `?${new URLSearchParams(
        Object.entries(options.query)
          .map(([key, value]) => [key, String(value)])
      ).toString()}`
    : '';

  return `${protocol}${host}/${normalizedPath}${queryString}`;
};

/**
 * Versão especializada para URLs com localização
 */
export const buildLocalizedUrl = (
  locale: string,
  path: string,
  options?: UrlBuilderOptions
) => {

  if (path === 'home') return buildCanonicalUrl(locale, options);
  if (path === '404') return buildCanonicalUrl(`${locale}/404`, options);

  return buildCanonicalUrl(`${locale}/${path}`, options);
};

/**
 * URL canônica de busca (`path?params`). Até a 3.x aceitava também `'amp'`
 * (`path.amp`); o AMP saiu na 4.0.0.
 */
export const buildSpecializedUrl = (
  type: 'search',
  path: string,
  params?: Record<string, string>,
  options?: UrlBuilderOptions
) => {
  const basePath = `${path}?${new URLSearchParams(params).toString()}`;

  return buildCanonicalUrl(basePath, { ...options, trailingSlash: false });
};